/**
 * PATH: apps/qa/src/pages/AIInsightsPage.tsx
 * PURPOSE: AI-powered insights for QA inspections
 *
 * WHY: Leverage AI to enhance QA workflows:
 * - Analyze evidence photos for potential issues
 * - Check pack readiness before generation
 * - Suggest template items based on standards
 *
 * FLOW:
 *   ┌─────────────┐    ┌──────────────┐    ┌─────────────┐
 *   │ Select      │───▶│ AI Analysis  │───▶│ Display     │
 *   │ Inspection  │    │ via DeepSeek │    │ Findings    │
 *   └─────────────┘    └──────────────┘    └─────────────┘
 *
 * DEPENDENCIES:
 *   - @quotech/api for API client
 *   - @tanstack/react-query for data fetching
 *   - ./ai-insights/*: extracted sub-components
 */

import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useAuthStore } from '@quotech/auth'
import { inspectionApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import type { Inspection } from '@/types/qa'
import type { Tab } from './ai-insights/types'
import { analyzeEvidence, checkReadiness } from './ai-insights/api'
import { EvidenceAnalysisResults } from './ai-insights/EvidenceAnalysisResults'
import { ReadinessCheckResults } from './ai-insights/ReadinessCheckResults'
import { TemplateSuggestionsPanel } from './ai-insights/TemplateSuggestionsPanel'
import {
  Sparkles,
  Brain,
  CheckCircle2,
  Lightbulb,
  FileSearch,
  Loader2,
  Image,
  ClipboardCheck,
  Target,
  Zap,
  Shield,
  Eye,
} from 'lucide-react'

// ── Tab configuration ───────────────────────────────────────────────────────

const TABS: { id: Tab; label: string; icon: typeof Brain; description: string }[] = [
  { id: 'evidence', label: 'Evidence Analysis', icon: Image, description: 'AI analyzes photos for quality issues' },
  { id: 'readiness', label: 'Pack Readiness', icon: ClipboardCheck, description: 'Check if inspection is ready for pack' },
  { id: 'suggestions', label: 'Template AI', icon: Lightbulb, description: 'Get AI-powered item suggestions' },
]

// ── Main Component ──────────────────────────────────────────────────────────

export default function AIInsightsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('evidence')
  const [selectedInspection, setSelectedInspection] = useState<string | null>(null)

  const organisationId = useAuthStore((s) => s.organisationId)
  const isAuthReady = !!organisationId

  const { data: inspectionsData, isLoading: loadingInspections } = useQuery({
    queryKey: ['qa-inspections-for-ai', organisationId],
    queryFn: () => inspectionApi.getInspections({ page_size: 100 }),
    enabled: isAuthReady,
  })

  const showLoadingInspections = loadingInspections || !isAuthReady

  const evidenceAnalysis = useMutation({ mutationFn: analyzeEvidence })
  const readinessCheck = useMutation({ mutationFn: checkReadiness })

  const handleAnalyze = () => {
    if (!selectedInspection) return
    if (activeTab === 'evidence') evidenceAnalysis.mutate(selectedInspection)
    else if (activeTab === 'readiness') readinessCheck.mutate(selectedInspection)
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 p-8 text-white">
        <div className="absolute inset-0 bg-grid-white/10" />
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -left-20 -bottom-20 h-64 w-64 rounded-full bg-purple-400/20 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-white/20 backdrop-blur">
              <Sparkles className="w-6 h-6" />
            </div>
            <span className="px-3 py-1 rounded-full bg-white/20 text-sm font-medium backdrop-blur">
              Powered by AI
            </span>
          </div>
          <h1 className="text-3xl font-bold mb-2">AI Quality Insights</h1>
          <p className="text-lg text-white/80 max-w-2xl">
            Leverage artificial intelligence to analyze evidence, verify compliance,
            and ensure your QA packs are complete before submission.
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-4 overflow-x-auto pb-2">
        {TABS.map((tab) => {
          const Icon = tab.icon
          const isActive = activeTab === tab.id
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-4 p-4 rounded-xl border-2 transition-all min-w-[280px]',
                isActive
                  ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30'
                  : 'border-border bg-card hover:border-violet-300 hover:bg-violet-50/50 dark:hover:bg-violet-950/10'
              )}
            >
              <div className={cn('p-3 rounded-xl', isActive ? 'bg-violet-500 text-white' : 'bg-muted text-muted-foreground')}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="text-left">
                <p className={cn('font-semibold', isActive ? 'text-violet-700 dark:text-violet-300' : 'text-foreground')}>
                  {tab.label}
                </p>
                <p className="text-sm text-muted-foreground">{tab.description}</p>
              </div>
            </button>
          )
        })}
      </div>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Selection Panel */}
        <div className="lg:col-span-1 space-y-4">
          <SelectionPanel
            inspections={inspectionsData?.results}
            isLoading={showLoadingInspections}
            selectedInspection={selectedInspection}
            onSelect={setSelectedInspection}
            onAnalyze={handleAnalyze}
            isAnalyzing={evidenceAnalysis.isPending || readinessCheck.isPending}
          />
          <AICapabilitiesInfo />
        </div>

        {/* Results Panel */}
        <div className="lg:col-span-2">
          {activeTab === 'evidence' && (
            <EvidenceAnalysisResults data={evidenceAnalysis.data} isLoading={evidenceAnalysis.isPending} error={evidenceAnalysis.error as Error | null} />
          )}
          {activeTab === 'readiness' && (
            <ReadinessCheckResults data={readinessCheck.data} isLoading={readinessCheck.isPending} error={readinessCheck.error as Error | null} />
          )}
          {activeTab === 'suggestions' && <TemplateSuggestionsPanel />}
        </div>
      </div>
    </div>
  )
}

// ── Selection Panel ─────────────────────────────────────────────────────────

function SelectionPanel({
  inspections,
  isLoading,
  selectedInspection,
  onSelect,
  onAnalyze,
  isAnalyzing,
}: {
  inspections?: Inspection[]
  isLoading: boolean
  selectedInspection: string | null
  onSelect: (id: string) => void
  onAnalyze: () => void
  isAnalyzing: boolean
}) {
  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
        <Target className="w-4 h-4 text-violet-500" />
        Select Inspection
      </h3>
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
        </div>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {inspections?.map((inspection: Inspection) => (
            <button
              key={inspection.id}
              onClick={() => onSelect(inspection.id)}
              className={cn(
                'w-full p-3 rounded-lg border text-left transition-all',
                selectedInspection === inspection.id
                  ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30'
                  : 'border-border hover:border-violet-300 hover:bg-accent'
              )}
            >
              <p className="font-medium text-sm text-foreground truncate">
                {inspection.inspection_name || 'Untitled'}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {inspection.category || 'No category'} • {inspection.status.replace(/_/g, ' ')}
              </p>
            </button>
          ))}
          {(!inspections || inspections.length === 0) && (
            <p className="text-sm text-muted-foreground text-center py-4">No inspections found</p>
          )}
        </div>
      )}
      <button
        onClick={onAnalyze}
        disabled={!selectedInspection || isAnalyzing}
        className={cn(
          'w-full mt-4 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all',
          'bg-gradient-to-r from-violet-600 to-purple-600 text-white',
          'hover:from-violet-700 hover:to-purple-700',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'shadow-lg shadow-violet-500/25 hover:shadow-xl hover:shadow-violet-500/30'
        )}
      >
        {isAnalyzing ? (
          <><Loader2 className="w-4 h-4 animate-spin" />Analyzing...</>
        ) : (
          <><Zap className="w-4 h-4" />Run Analysis</>
        )}
      </button>
    </div>
  )
}

// ── AI Capabilities Info ────────────────────────────────────────────────────

function AICapabilitiesInfo() {
  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-xl border border-border p-6">
      <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
        <Brain className="w-4 h-4 text-violet-500" />
        How AI Helps
      </h4>
      <ul className="space-y-3 text-sm text-muted-foreground">
        <li className="flex items-start gap-2"><Shield className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /><span>Identifies potential compliance issues from photos</span></li>
        <li className="flex items-start gap-2"><Eye className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" /><span>Detects workmanship concerns humans might miss</span></li>
        <li className="flex items-start gap-2"><FileSearch className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" /><span>References AS/NZS standards automatically</span></li>
        <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" /><span>Validates pack completeness before submission</span></li>
      </ul>
    </div>
  )
}
